import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Bell, Settings } from 'lucide-react';
import { request } from '../api/client';
import { Dropdown, IconButton, CountBadge, Tooltip, cn } from '../ui';

interface Notif {
  id: string;
  title: string;
  body: string;
  channel: string;
  readAt: string | null;
  createdAt: string;
  data?: any;
}

// Short relative time ("3m", "2h", "5d").
function relTime(iso: string): string {
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.max(s, 1)}s`;
  const m = Math.round(s / 60); if (m < 60) return `${m}m`;
  const h = Math.round(m / 60); if (h < 24) return `${h}h`;
  const d = Math.round(h / 24); if (d < 30) return `${d}d`;
  return `${Math.round(d / 30)}mo`;
}

const headerIconBtn =
  'border border-line text-content/70 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:hover:bg-brand-900/20';

/**
 * The header bell: opens a dropdown of the user's in-app notifications (GET /notifications),
 * with an unread count badge, click-to-mark-read, mark-all-read, and a shortcut to the
 * notification *settings* page (which also lives in the user menu).
 */
export function NotificationBell() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['notifications-feed'],
    queryFn: () => request<Notif[]>({ url: '/notifications' }),
    refetchInterval: 45_000,
    refetchOnWindowFocus: true,
  });
  const items = data ?? [];
  const unread = useMemo(() => items.filter((n) => !n.readAt).length, [items]);

  const markRead = useMutation({
    mutationFn: (id: string) => request({ url: `/notifications/${id}/read`, method: 'PATCH' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications-feed'] }),
  });
  const markAllRead = useMutation({
    mutationFn: () => Promise.all(
      items.filter((n) => !n.readAt).map((n) => request({ url: `/notifications/${n.id}/read`, method: 'PATCH' })),
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications-feed'] }),
  });

  const onItemClick = (n: Notif) => {
    if (!n.readAt) markRead.mutate(n.id);
    const link = n.data?.link ?? n.data?.url;
    if (typeof link === 'string' && link.startsWith('/')) navigate(link);
  };

  return (
    <Dropdown
      trigger="click"
      align="end"
      panelClassName="w-[360px] max-w-[92vw] p-0 overflow-hidden"
      panel={
        <div className="flex max-h-[70vh] flex-col">
          <div className="flex items-center justify-between border-b border-line px-3.5 py-2.5">
            <span className="text-sm font-semibold text-content">
              Notifications{unread ? <span className="ml-1 text-brand-600">· {unread} new</span> : null}
            </span>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); markAllRead.mutate(); }}
                  className="rounded px-1.5 py-0.5 text-[11px] font-medium text-brand-600 hover:bg-surface-2"
                >
                  Mark all read
                </button>
              )}
              <Tooltip label="Notification settings">
                <button
                  type="button"
                  onClick={() => navigate('/profile/notifications')}
                  className="grid size-7 place-items-center rounded text-subtle hover:bg-surface-2 hover:text-content"
                >
                  <Settings className="size-4" />
                </button>
              </Tooltip>
            </div>
          </div>

          <div className="overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-subtle">You're all caught up.</div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => onItemClick(n)}
                  className={cn(
                    'flex w-full gap-2.5 border-b border-line/60 px-3.5 py-2.5 text-left transition-colors hover:bg-surface-2',
                    !n.readAt && 'bg-brand-50/40 dark:bg-brand-900/10',
                  )}
                >
                  <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', n.readAt ? 'bg-transparent' : 'bg-brand-500')} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline justify-between gap-2">
                      <span className={cn('truncate text-[13px] text-content', n.readAt ? 'font-medium' : 'font-semibold')}>{n.title}</span>
                      <span className="shrink-0 text-[10px] text-subtle">{relTime(n.createdAt)}</span>
                    </span>
                    <span className="mt-0.5 block whitespace-pre-line break-words text-[12px] leading-snug text-subtle line-clamp-3">{n.body}</span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      }
    >
      <span className="relative inline-flex">
        <Tooltip label="Notifications">
          <IconButton aria-label="Notifications" className={headerIconBtn}>
            <Bell />
          </IconButton>
        </Tooltip>
        {unread > 0 && <CountBadge count={unread} className="pointer-events-none absolute -right-1 -top-1" />}
      </span>
    </Dropdown>
  );
}
